import { useState } from 'react'
import { Prose } from '../../components/Prose'
import { useActivities, useAddActivity, useSchedule, useSessions } from '../../api/hooks'
import { todayIso } from '../../lib/format'
import {
  GUN_KISA, MARK_LABEL, buildMonth, buildWeek, monthLabel, monthStart,
  shiftDate, shiftMonth, summarize, weekStartMon, type CalendarDay,
} from '../../lib/calendar'
import { logSummary, type Session } from '../../lib/session'
import { ACTIVITIES, ACTIVITY_BY_KEY } from '../../lib/energy'

/**
 * Antrenman takvimi.
 *
 * Önce burada 15 satırlık bir liste vardı. Liste "son ne yaptım" sorusuna
 * cevap veriyor ama asıl sorulan başka: "geçen salı ne yaptım", "bu ay kaç gün
 * çalıştım", "hangi günü boş geçtim". Bunlar takvim soruları; listede aranmıyor,
 * ızgarada görülüyor.
 *
 * İkinci işi de geriye dönük giriş. Boş bir güne dokunup doldurmak, formda
 * "hangi tarihti" diye düşünmekten kolay — ve o gün ekranda zaten önünde.
 */
export function WorkoutCalendar({ onPick }: { onPick: (date: string) => void }) {
  const sessions = useSessions()
  const activities = useActivities()
  const sched = useSchedule()

  const today = todayIso()
  const [gorunum, setGorunum] = useState<'ay' | 'hafta'>('ay')
  const [anchor, setAnchor] = useState(today)
  const [acik, setAcik] = useState<string | null>(null)

  if (sessions.isLoading || sched.isLoading) {
    return <div className="card"><p className="sub">Takvim yükleniyor…</p></div>
  }

  // Şablondaki kuvvet günleri — boş bırakılan bir kuvvet gününü işaretlemek için.
  const strengthWeekdays = (sched.data?.template ?? [])
    .filter((t) => t.kind === 'strength')
    .map((t) => t.weekday)

  const girdi = {
    anchor,
    today,
    sessions: sessions.data ?? [],
    activities: activities.data ?? [],
    strengthWeekdays,
  }
  const gunler = gorunum === 'ay' ? buildMonth(girdi) : buildWeek(girdi)
  const ozet = summarize(gunler)

  const kaydir = (yon: number) =>
    setAnchor(gorunum === 'ay' ? shiftMonth(monthStart(anchor), yon) : shiftDate(weekStartMon(anchor), yon * 7))

  const secili = acik ? gunler.find((g) => g.date === acik) : null
  const seansi = (d: string) => (sessions.data ?? []).find((s) => s.date === d)

  return (
    <>
      <div className="card">
        <div className="calhead">
          <button onClick={() => kaydir(-1)} aria-label="Önceki">‹</button>
          <h2 style={{ margin: 0 }}>
            {gorunum === 'ay'
              ? monthLabel(monthStart(anchor))
              : `${weekStartMon(anchor).slice(8)}–${shiftDate(weekStartMon(anchor), 6).slice(8)} ${monthLabel(monthStart(anchor))}`}
          </h2>
          <button onClick={() => kaydir(1)} aria-label="Sonraki">›</button>
        </div>

        <div className="segbar" role="tablist" aria-label="Takvim görünümü" style={{ marginTop: 10 }}>
          {(['ay', 'hafta'] as const).map((g) => (
            <button key={g} role="tab" aria-selected={gorunum === g}
                    onClick={() => { setGorunum(g); setAnchor(today) }}>
              {g === 'ay' ? 'Ay' : 'Hafta'}
            </button>
          ))}
        </div>

        <div className="calgrid" role="grid">
          {GUN_KISA.map((g) => <div className="calgun" key={g}>{g}</div>)}
          {gunler.map((g) => (
            <button
              key={g.date}
              className="calday"
              data-mark={g.mark}
              data-outside={g.outside || undefined}
              data-today={g.today || undefined}
              data-secili={acik === g.date || undefined}
              aria-label={`${g.date} — ${MARK_LABEL[g.mark]}`}
              onClick={() => setAcik(acik === g.date ? null : g.date)}
            >
              <span className="calno">{g.day}</span>
              {g.activityCount > 0 && g.mark !== 'activity' && <span className="caldot" />}
            </button>
          ))}
        </div>

        <div className="callegend">
          <span><i data-mark="done" /> tamamlandı {ozet.done}</span>
          <span><i data-mark="partial" /> yarım {ozet.partial}</span>
          <span><i data-mark="activity" /> aktivite {ozet.activity}</span>
          <span><i data-mark="missed" /> boş geçti {ozet.missed}</span>
        </div>

        <Prose className="note">
          Bir güne dokun: o günün dökümü açılır. Kayıt yoksa geriye dönük
          girebilirsin — antrenmanı yaptığın güne yazmak önemli, çünkü ilerleme
          grafikleri ve "geçen sefer ne yaptın" satırı tarihe bakıyor.
        </Prose>
      </div>

      {secili && (
        <GunDetay
          gun={secili}
          session={seansi(secili.date)}
          aktiviteler={(activities.data ?? []).filter((a) => a.date === secili.date)}
          onPick={onPick}
        />
      )}
    </>
  )
}

function GunDetay({ gun, session, aktiviteler, onPick }: {
  gun: CalendarDay
  session: Session | undefined
  aktiviteler: { id: number; activityKey: string; minutes: number }[]
  onPick: (date: string) => void
}) {
  const yapilmis = session?.exercises.filter((e) => e.sets.some((s) => s.done)) ?? []

  return (
    <div className="card">
      <h2>{trGun(gun.date)}</h2>

      {yapilmis.length > 0 ? (
        <>
          <p className="sub" style={{ marginTop: 0 }}>
            Antrenman {session!.day}
            {session!.completedAt === null && <span className="pill" style={{ marginLeft: 6 }}>yarım kaldı</span>}
          </p>
          <table>
            <thead><tr><th>Hareket</th><th>Setler</th></tr></thead>
            <tbody>
              {yapilmis.map((e) => (
                <tr key={e.slug}>
                  <td>{e.name}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{logSummary(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p className="sub" style={{ marginTop: 0 }}>
          {gun.future
            ? 'Henüz gelmedi.'
            : 'Bu güne kuvvet antrenmanı yazılmamış.'}
        </p>
      )}

      <h3>Aktiviteler</h3>
      {aktiviteler.length > 0 ? (
        <ul className="tight">
          {aktiviteler.map((a) => (
            <li key={a.id}>{ACTIVITY_BY_KEY[a.activityKey]?.name ?? a.activityKey} · {a.minutes} dk</li>
          ))}
        </ul>
      ) : (
        <p className="sub" style={{ marginTop: 0 }}>Bu güne aktivite yazılmamış.</p>
      )}

      {/* Aktivite formu Enerji bölümünde de var ama oraya gitmek "hangi tarihti"
          sorusunu geri getiriyor. Gün zaten ekranda; buradan eklemek doğal. */}
      {!gun.future && <AktiviteEkle date={gun.date} />}

      {/* Gelecek bir güne antrenman yazmanın anlamı yok — yapılmamış bir şeyi
          kaydetmek veriyi bozar. Bugün ve öncesi açık. */}
      {!gun.future && (
        <button className="primary" onClick={() => onPick(gun.date)}>
          {yapilmis.length > 0 ? 'Bu günü düzenle' : 'Bu güne antrenman gir'}
        </button>
      )}
    </div>
  )
}

/** Gün detayının içindeki küçük aktivite formu. */
function AktiviteEkle({ date }: { date: string }) {
  const add = useAddActivity()
  const [key, setKey] = useState('tennis_singles')
  const [dk, setDk] = useState('60')
  const [kcal, setKcal] = useState('')

  const gruplar = [...new Set(ACTIVITIES.map((a) => a.group))]

  return (
    <div className="row" style={{ marginTop: 10 }}>
      <div style={{ flex: '2 1 200px' }}>
        <label className="f">Aktivite</label>
        <select value={key} onChange={(e) => setKey(e.target.value)}>
          {gruplar.map((g) => (
            <optgroup key={g} label={g}>
              {ACTIVITIES.filter((a) => a.group === g).map((a) => (
                <option key={a.key} value={a.key}>{a.name}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <div style={{ flex: '0 1 100px' }}>
        <label className="f">Dakika</label>
        <input type="number" min={1} max={600} inputMode="numeric"
               value={dk} onChange={(e) => setDk(e.target.value)} />
      </div>
      <div style={{ flex: '0 1 150px' }}>
        <label className="f">Kalori</label>
        <input type="number" min={0} max={5000} inputMode="numeric" placeholder="boş = tahmin"
               value={kcal} onChange={(e) => setKcal(e.target.value)} />
      </div>
      <button disabled={add.isPending || !dk}
              onClick={() => {
                const m = Number(dk)
                if (!m || m < 1) return
                add.mutate({
                  date, activityKey: key, minutes: m,
                  kcalOverride: kcal.trim() === '' ? null : Math.max(0, Number(kcal) || 0),
                })
                setKcal('')
              }}>
        {add.isPending ? 'Ekleniyor…' : 'Aktivite ekle'}
      </button>
    </div>
  )
}

const GUNLER = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']

function trGun(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`)
  return `${iso.slice(8)}.${iso.slice(5, 7)}.${iso.slice(0, 4)} · ${GUNLER[d.getUTCDay()]}`
}
